import { ROUTE_PATHS } from './constants';

const MAX_AUTH_RETURN_TARGET_LENGTH = 2048;
const SAFE_RETURN_QUERY_KEYS = new Set([
  'tab',
  'view',
  'period',
  'status',
  'branchScope',
  'branchId',
  'filter',
]);
const SAFE_RETURN_QUERY_VALUE = /^[A-Za-z0-9._~-]{1,80}$/;

export const LOGIN_RECOVERY_REASONS = [
  'auth-required',
  'session-expired',
  'verification-required',
] as const;

export type LoginRecoveryReason = (typeof LOGIN_RECOVERY_REASONS)[number];

export function safeInternalPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return null;
  }

  try {
    const parsed = new URL(value, 'https://invento.invalid');
    return parsed.origin === 'https://invento.invalid' ? `${parsed.pathname}${parsed.search}${parsed.hash}` : null;
  } catch {
    return null;
  }
}

export function resolveSafeReturnTarget(
  value: string | null | undefined,
  fallback: string,
): string {
  return safeInternalPath(value) ?? fallback;
}

/** A safe destination for post-login navigation. */
export function safeAuthReturnTarget(value: string | null | undefined): string | null {
  const target = safeInternalPath(value);
  if (!target) {
    return null;
  }

  // A return target is user-visible in the login URL and browser history.
  // Keep only bounded, harmless view state (for example `tab=stock`) before
  // carrying a route across re-auth.
  const parsed = new URL(target, 'https://invento.invalid');
  if (parsed.pathname === ROUTE_PATHS.LOGIN || parsed.pathname === ROUTE_PATHS.REGISTER) {
    return null;
  }
  for (const [key, value] of [...parsed.searchParams.entries()]) {
    if (!SAFE_RETURN_QUERY_KEYS.has(key) || !SAFE_RETURN_QUERY_VALUE.test(value)) {
      parsed.searchParams.delete(key);
    }
  }
  // Fragments are not needed to resume the route and can carry arbitrary
  // user-generated data that would otherwise be exposed in the login URL.
  parsed.hash = '';
  const sanitized = `${parsed.pathname}${parsed.search}`;
  return sanitized.length <= MAX_AUTH_RETURN_TARGET_LENGTH ? sanitized : null;
}

export function parseLoginRecoveryReason(
  value: string | null | undefined,
): LoginRecoveryReason | null {
  return LOGIN_RECOVERY_REASONS.includes(value as LoginRecoveryReason)
    ? (value as LoginRecoveryReason)
    : null;
}

export function protectedRouteRecoveryReason(pathname: string): LoginRecoveryReason {
  return pathname === ROUTE_PATHS.VERIFY_EMAIL || pathname === ROUTE_PATHS.VERIFY_PHONE
    ? 'verification-required'
    : 'auth-required';
}

export function buildLoginRecoveryPath(options: {
  returnTo?: string | null;
  reason?: LoginRecoveryReason;
} = {}): string {
  const params = new URLSearchParams();
  params.set('reason', options.reason ?? 'auth-required');

  const returnTo = safeAuthReturnTarget(options.returnTo);
  if (returnTo) params.set('returnTo', returnTo);

  return `${ROUTE_PATHS.LOGIN}?${params.toString()}`;
}

export function currentBrowserPath(): string | null {
  if (typeof window === 'undefined') return null;
  return safeAuthReturnTarget(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
}

/** Carries a validated target through a policy-required auth step. */
export function buildRouteWithReturnTo(
  routePath: string,
  returnTo: string | null | undefined,
): string {
  const target = safeAuthReturnTarget(returnTo);
  const targetPath = target ? new URL(target, 'https://invento.invalid').pathname : null;
  if (!target || targetPath === routePath) return routePath;
  return `${routePath}?returnTo=${encodeURIComponent(target)}`;
}

/** Verification routes are recovery steps, not final destinations. */
export function safePostVerificationTarget(value: string | null | undefined): string | null {
  const target = safeAuthReturnTarget(value);
  const targetPath = target ? new URL(target, 'https://invento.invalid').pathname : null;
  return targetPath === ROUTE_PATHS.VERIFY_EMAIL || targetPath === ROUTE_PATHS.VERIFY_PHONE
    ? null
    : target;
}
